import { useState, useRef, useEffect, useCallback } from 'react';

type UseControllableValueProps = {
  value?: string | null;
  defaultValue?: string | null;
  onChange?: (value: string | null) => void;
  isLoading?: boolean;
};

export const useControllableValue = ({
  value: valueProp,
  defaultValue: defaultValueProp,
  onChange: onChangeProp,
  isLoading,
}: UseControllableValueProps) => {
  const isControlled = valueProp !== undefined;

  const [internalValue, setInternalValue] = useState<string | null>(
    valueProp ?? defaultValueProp ?? null,
  );

  useEffect(() => {
    if (!isControlled && defaultValueProp !== undefined) {
      setInternalValue(defaultValueProp);
    }
  }, [isControlled, defaultValueProp]);

  // When isLoading transitions true→false the server has responded and
  // defaultValueProp reflects the authoritative value. Force-sync even if
  // defaultValueProp didn't change (server sanitized to the same value).
  const prevIsLoadingRef = useRef(isLoading);
  useEffect(() => {
    if (
      prevIsLoadingRef.current &&
      !isLoading &&
      !isControlled &&
      defaultValueProp !== undefined
    ) {
      setInternalValue(defaultValueProp);
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading, isControlled, defaultValueProp]);

  const localValue = isControlled ? valueProp ?? null : internalValue;

  const setLocalValue = useCallback(
    (next: string | null) => {
      if (!isControlled) {
        setInternalValue(next);
      }
      onChangeProp?.(next);
    },
    [isControlled, onChangeProp],
  );

  return { localValue, setLocalValue, isControlled };
};
